import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { CalendarPlanSlot, ConceptVersionsResponse } from "@/types/contentCalendar.type";
import { planWriteKey, reconcilePlan, settlePlanWrite } from "./calendarCache";
import { clampWhenHidden } from "@/lib/realtime";
import { livePoll, useCalendarLive } from "./calendarLive";

export const conceptVersionsKey = (slotId: string | null) => ["conceptVersions", slotId];

export function useConceptVersions(slotId: string | null) {
  const live = useCalendarLive();
  return useQuery({
    queryKey: conceptVersionsKey(slotId),
    queryFn: async () => (await axiosInstance.get<ConceptVersionsResponse>(`/content-calendar/plan-slots/${slotId}/concept-versions`)).data,
    enabled: !!slotId,
    refetchInterval: () => livePoll(live, clampWhenHidden(10_000)),
    refetchOnWindowFocus: !live,
  });
}

export function useConceptRevision(planId: string, slotId: string) {
  const qc = useQueryClient();
  const resync = () => {
    void qc.invalidateQueries({ queryKey: conceptVersionsKey(slotId) });
    reconcilePlan(qc, planId);
  };
  const report = (err: unknown) => queue.add({ title: "Could not save this change", description: getAiRequestErrorMessage(err, "Your draft is still here. Please try again."), variant: "error" });
  const saveAlignment = useMutation({
    mutationFn: async (guidance: string) => (await axiosInstance.patch(`/content-calendar/plan-slots/${slotId}/revision-alignment`, { guidance: guidance.trim() })).data,
    onSuccess: resync,
    onError: report,
  });
  const reviseExisting = useMutation({
    mutationKey: [...planWriteKey(planId), "reviseExisting"],
    mutationFn: async () => (await axiosInstance.post<{ slot: CalendarPlanSlot }>(`/content-calendar/plan-slots/${slotId}/ideas/revise-existing`, {})).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: conceptVersionsKey(slotId) }),
    onSettled: (result, err) => settlePlanWrite(qc, planId, err ? { refetch: true } : { slot: result?.slot }),
    onError: report,
  });
  const addComment = useMutation({
    mutationFn: async ({ versionId, body, visibility }: { versionId: string; body: string; visibility?: "SHARED" | "INTERNAL" }) => (await axiosInstance.post(`/content-calendar/plan-slots/${slotId}/concept-versions/${versionId}/comments`, { body, ...(visibility ? { visibility } : {}) })).data,
    onSuccess: resync,
    onError: report,
  });
  return { saveAlignment, reviseExisting, addComment };
}
